import React, { useState, useEffect } from 'react';
import LeftDashboard from '../components/LeftDashboard';
import { fetchStats, fetchCompanies } from '../api';

function Bar({ label, val, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
      <div style={{ width: 80, fontSize: 11, fontWeight: 600, color: 'var(--text2)', textAlign: 'right' }}>{label}</div>
      <div style={{ flex: 1, background: 'var(--border)', height: 16, borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ width: `${val}%`, background: color, height: '100%', borderRadius: 8, display: 'flex', alignItems: 'center', paddingLeft: 6 }}>
          <span style={{ color: '#fff', fontSize: 9, fontWeight: 700 }}>{val}%</span>
        </div>
      </div>
    </div>
  );
}

function Offender({ name, rate, color = 'var(--text)' }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontWeight: 600, color: 'var(--text2)', fontSize: 11 }}>{name}</span>
      <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color, fontSize: 11 }}>{rate}%</span>
    </div>
  );
}

export default function OfferRatePage({ user, isEmbedded, view, setView, setSelectedCompany, refreshSignal, preloadedStats }) {
  const [stats, setStats] = useState(preloadedStats || null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        if (preloadedStats) {
           setStats(preloadedStats);
           const c = await fetchCompanies({ limit: 50 });
           setCompanies(c.companies || []);
        } else {
           const [s, c] = await Promise.all([fetchStats(), fetchCompanies({ limit: 50 })]);
           setStats(s.stats);
           setCompanies(c.companies || []);
        }
      } catch (err) {
        console.error('Failed to load offer rate data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [refreshSignal, preloadedStats]);

  const highestOfferRates = [...companies].filter(c => c.totalReviews > 2).sort((a, b) => (b.offerRate || 0) - (a.offerRate || 0)).slice(0, 5);
  const selectiveCompanies = [...companies].filter(c => c.totalReviews > 2).sort((a, b) => (a.offerRate || 0) - (b.offerRate || 0)).slice(0, 5);

  const content = (
    <main style={{ minWidth: 0, animation: 'fadeUp 0.4s ease-out' }}>

      <div style={{
        background: 'linear-gradient(135deg, var(--green), #059669)',
        padding: '16px 24px', borderRadius: 12, color: '#fff',
        boxShadow: '0 8px 16px -4px rgba(16,185,129,0.3)',
        marginBottom: 12, position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', right: -10, top: -20, fontSize: 80, opacity: 0.1 }}>✅</div>
        <h1 style={{ fontFamily: 'var(--display)', fontSize: 28, fontWeight: 900, letterSpacing: -0.5, margin: '0 0 2px' }}>
            {loading ? '...' : (stats?.offerRate || 38)}%
        </h1>
        <h2 style={{ fontSize: 13, fontWeight: 600, opacity: 0.9, margin: 0, fontFamily: 'var(--sans)' }}>Platform Offer Rate</h2>
      </div>

      <div style={{ background: 'var(--surface)', padding: 12, borderRadius: 10, border: '1px solid var(--border)', marginBottom: 12 }}>
        <h3 style={{ fontSize: 13, margin: '0 0 12px', color: 'var(--text)' }}>Standard Conversion Benchmarks</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Bar label="Phone / OA" val={12} color="var(--red)" />
          <Bar label="Round 1" val={45} color="var(--orange)" />
          <Bar label="Round 2" val={62} color="var(--blue)" />
          <Bar label="Manager / HR" val={85} color="var(--green)" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div style={{ background: 'var(--surface)', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 12, color: 'var(--text)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Highest Confidence</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {highestOfferRates.map(c => (
              <Offender key={c.id || c.name} name={c.name} rate={c.offerRate || 0} color="var(--green)" />
            ))}
            {!loading && highestOfferRates.length === 0 && <div style={{ fontSize: 11, color: 'var(--text3)' }}>No data available</div>}
          </div>
        </div>
        <div style={{ background: 'var(--surface)', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 12, color: 'var(--text)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Most Selective</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {selectiveCompanies.map(c => (
              <Offender key={c.id || c.name} name={c.name} rate={c.offerRate || 0} color="var(--red)" />
            ))}
            {!loading && selectiveCompanies.length === 0 && <div style={{ fontSize: 11, color: 'var(--text3)' }}>No data available</div>}
          </div>
        </div>
      </div>
    </main>
  );

  if (isEmbedded) return content;

  return (
    <div style={{
      maxWidth: 1100, margin: '0 auto', padding: '16px 24px',
      display: 'grid', gridTemplateColumns: '220px 1fr', gap: 28,
      alignItems: 'start'
    }}>
      <LeftDashboard user={user} view={view} setView={setView} setSelectedCompany={setSelectedCompany} />
      {content}
    </div>
  );
}